import { useActivitySession } from './use-activity-session';
import { AuthorizedView } from './views/AuthorizedView';
import { NotAuthorizedView } from './views/NotAuthorizedView';
import { UnlinkedView } from './views/UnlinkedView';
import { PreviewHarness, getPreviewViewFromUrl } from './dev/PreviewHarness';

/**
 * Top-level entry. In dev mode only, `?preview=tester|tester-admin|unlinked|...`
 * short-circuits to `<PreviewHarness>` and renders the requested view against
 * fixture data — `import.meta.env.DEV` ensures this branch is dead code in
 * production builds. The split keeps `<ActivityRouter>`'s hook calls
 * unconditional (React rules-of-hooks).
 */
export function App() {
  const previewView = getPreviewViewFromUrl();
  if (previewView) return <PreviewHarness view={previewView} />;
  return <ActivityRouter />;
}

/**
 * Real Activity router. Calls `useActivitySession` once on mount, which drives
 * the SDK auth handshake → backend role resolution. Renders one of four views
 * based on the resolved discriminant.
 *
 * Loading + error states render terse status text — the iframe is small
 * (~640px wide inside Discord's panel layout), and the SDK handshake usually
 * completes in < 1s, so a heavyweight skeleton would be jarring.
 *
 * The `'unlinked'` discriminant routes to `<UnlinkedView>`, an onboarding
 * screen that hands the user off to the web account-settings page where
 * they can connect their Discord account.
 */
function ActivityRouter() {
  const session = useActivitySession();

  if (session.isError) {
    return (
      <div className="view view--error">
        <h1>Couldn't load the Activity</h1>
        <p>{session.error.message}</p>
      </div>
    );
  }

  if (!session.isSuccess) {
    return <div className="view view--loading">Connecting to Discord…</div>;
  }

  const { view, discordUserId, accessToken, capabilities, linkedUser } = session.data;

  // Every authorized role lands on the same consolidated surface — admins
  // get inline widgets (FM tile for everyone, stuck-jobs strip + "Open jobs
  // in admin" header action gated on isAdmin) rather than a separate Ops
  // Console flip (#4172).
  switch (view) {
    case 'tester':
    case 'admin':
      // The resolve contract (`DiscordActivityResolveResponse`) populates
      // `linkedUser` whenever `view !== 'unlinked'`, so the tester / admin
      // branches are guaranteed to have it. The runtime check here is a
      // type-narrowing assertion against the optional field — a missing
      // payload at this point would be a server contract violation.
      if (!linkedUser) {
        throw new Error(
          `Discord Activity resolve returned no linkedUser for view='${view}' — backend contract violation`,
        );
      }
      return (
        <AuthorizedView
          discordUserId={discordUserId}
          accessToken={accessToken}
          capabilities={capabilities}
          linkedUser={linkedUser}
        />
      );
    case 'unlinked':
      return (
        <UnlinkedView
          onCheckAgain={() => void session.refetch()}
          isChecking={session.isFetching}
        />
      );
    case 'unauthorized':
      return <NotAuthorizedView />;
  }
}
